import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, withStyles } from '@material-ui/styles';
import { CloseIcon, SelectInput } from 'components';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  FormControl,
  TextField,
  Tooltip,
  InputLabel,
  ListItemText,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { fontFamilies } from './constants';
import { useDispatch, useSelector } from 'react-redux';
import { ProductType } from 'constants.js';
import { Info } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { SkeletonCardFields } from 'components/Loading';
import { getAllLists, getAllSegmentedLists } from 'store/lists/actions';
import { getEnvVar } from 'helpers';
import { getEventTypeOptions } from 'helpers/templateHelper';

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'unset'
  },
  cardHeader: {},
  radioLabel: {
    marginLeft: 0,
    alignItems: 'baseline'
  },
  logoText: {
    display: 'flex',
    flexDirection: 'row-reverse'
  },
  cardActions: {
    flexDirection: 'row-reverse'
  },
  item: {
    display: 'flex',
    alignItems: 'center'
  },
  infoIcon: {
    marginLeft: '16px'
  },
  imageGrid: {
    width: '80%'
  },
  preview: {
    height: '64px',
    maxWidth: '250px',
    maxHeight: '250px',
    margin: '5px 0'
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  radioGroup: {
    paddingTop: '8px',
    marginLeft: '8px'
  },
  tooltip: {
    marginTop: '16px',
    marginLeft: '12px'
  },
  autocomplete: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
}));

const LightTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11
  }
}))(Tooltip);

const Details = props => {
  const { className, staticContext, survey, next, showNav, ...rest } = props;
  const {
    thisSurvey,
    thisSurveyPreview,
    handleChange,
    handleAutomationChange,
    handleLogoChange,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    supportedInternalSurveyResponseTypeOptions,
    updateCurrentErrorValidationSegment,
    isSurveyLoading,
    goNext,
    closeSurvey,
    isTemplateReadOnly,
    handleTypeChange,
    handleSubscriberListChange
  } = survey;

  const [subscribersList, setSubscribersList] = useState([]);
  useEffect(() => {
    updateCurrentErrorValidationSegment('details');

    let options = [];

    options = supportedInternalSurveyResponseTypeOptions?.filter(
      option => option.type === 'EVENT_TYPE_CATEGORY'
    );

    if (
      options.length === 1 &&
      (typeof thisSurvey.schemaIdentifier === 'undefined' ||
        thisSurvey.schemaIdentifier === '') &&
      thisSurvey.productType !== ProductType.List
    ) {
      handleAutomationChange({
        target: {
          name: 'schemaIdentifier',
          value: options[0]?.schemaIdentifier
        }
      });
    }
  }, [thisSurvey, updateCurrentErrorValidationSegment]);

  const classes = useStyles();
  const dispatch = useDispatch();

  const isSurveySaved = () => !!thisSurvey.id;
  const isUnsavedCopy = () => !!thisSurvey._isCopy && !isSurveySaved();
  const isCopyFlow = () => !!(thisSurvey._isCopy || thisSurvey._isCopyTemplate);

  const areInitialSettingsDisabled = () =>
    !!thisSurvey.id || !!thisSurvey.details;

  const isSubscriberListSelectionDisabled = () =>
    ((!!thisSurvey.id || !!thisSurvey.details) && !isUnsavedCopy());

  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const allLists = useSelector(store => store.lists.allLists.current);
  const allSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.current
  );
  const gettingAllLists = useSelector(store => store.lists.allLists.getting);
  const gettingAllSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.getting
  );

  useEffect(() => {
    if (
      thisSurvey.productType === ProductType.List &&
      allLists.length === 0 &&
      allSegmentedLists.length === 0
    ) {
      dispatch(getAllLists());
      dispatch(getAllSegmentedLists());
    }
  }, [thisSurvey.productType]);

  useEffect(() => {
    if (
      !gettingAllLists &&
      !gettingAllSegmentedLists &&
      (allSegmentedLists.length > 0 || allLists.length > 0)
    ) {
      let listsMap = {};
      allLists.map(list => {
        listsMap[list.id] = {
          ...list,
          searchText: list.name.toLowerCase(),
          children: []
        };
      });
      allSegmentedLists.map(segList => {
        let parentList = listsMap[segList.listId];
        parentList.searchText = `${
          parentList.searchText
        }.${segList.name.toLowerCase()}`;
        parentList.children = [
          ...parentList.children,
          { ...segList, searchText: segList.name.toLowerCase() }
        ];
      });
      let finalList = [];
      Object.values(listsMap).forEach(list => {
        finalList.push(list);
        if (list.children.length > 0) {
          const sortedList = [...list.children].sort((list1, list2) =>
            list1.name < list2.name ? -1 : 1
          );
          finalList.push(...sortedList);
        }
      });
      setSubscribersList(finalList);
    }
  }, [allSegmentedLists, allLists]);

  const level1 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(1),
      pointerEvents: 'none',
      backgroundColor: '#A5A5A5',
      '&:hover': {
        backgroundColor: '#A5A5A5'
      }
    }
  }))();

  const level2 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(3),
      pointerEvents: 'none',
      backgroundColor: '#CECECE',
      '&:hover': {
        backgroundColor: '#CECECE'
      }
    }
  }))();

  const level3 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(5),
      pointerEvents: 'none',
      backgroundColor: '#E5E5E5',
      '&:hover': {
        backgroundColor: '#E5E5E5'
      }
    }
  }))();

  const level4 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(7),
      backgroundColor: '#FFFFFF'
    }
  }))();

  const iconStyle = makeStyles(() => ({
    root: {
      minWidth: '30px'
    }
  }))();

  const segListStyle = makeStyles(theme => ({
    root: {
      marginLeft: theme.spacing(1),
      color: 'grey'
    }
  }))();

  const filterSubscribersList = (options, state) => {
    let searchInput = state.inputValue.toLowerCase();
    return options.filter(option => option.searchText.includes(searchInput));
  };

  const getSubscribersListValue = () => {
    let selectedList = subscribersList.filter(list =>
      thisSurvey.segmentedListId && thisSurvey.segmentedListId !== ''
        ? list.id === thisSurvey.segmentedListId
        : list.id === thisSurvey.subscriberListId
    );
    return selectedList.length === 1 ? selectedList[0] : '';
  };

  const surveyFontFamily = {
    list: fontFamilies,
    keyId: 'value',
    valueId: 'value',
    displayValueId: 'label'
  };

  const handleSingleResponseChange = () => {
    handleChange({
      target: {
        value: !thisSurvey.singleResponse,
        name: 'singleResponse'
      }
    });
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <div className={classes.headerContainer}>
            <div className={classes.title}>
              <CardHeader
                subheader="* indicates a required field"
                title="Survey Details"
                titleTypographyProps={{ variant: 'h1' }}
              />
              {isTemplateReadOnly() && (
                <Tooltip
                  title={
                    "You do not have access to edit this survey. Tenant Default surveys can be edited only with 'ALL' property."
                  }>
                  <InfoIcon size="small" style={{ marginTop: '26px' }} />
                </Tooltip>
              )}
            </div>
            <CloseIcon onClickClose={closeSurvey} />
          </div>
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={8} xs={12}>
                <Grid item>
                  <TextField
                    fullWidth
                    label="Survey Name"
                    margin="dense"
                    name="name"
                    onChange={handleChange}
                    required
                    disabled={isTemplateReadOnly()}
                    value={thisSurvey.name || ''}
                    variant="outlined"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={shouldShowError('name')}
                    helperText={getErrorMessage('name')}
                  />
                </Grid>

                {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' &&
                  getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_SURVEYS') ===
                    'true' && (
                    <Grid item xs={12}>
                      <FormControl
                        component="fieldset"
                        error={shouldShowError('productType')}>
                        <FormControlLabel
                          label="Type:"
                          labelPlacement="start"
                          style={{ marginBottom: '0px' }}
                          control={
                            <RadioGroup
                              className={classes.radioGroup}
                              aria-label="productType"
                              name="productType"
                              value={
                                thisSurvey.productType || ProductType.Product
                              }
                              onChange={handleTypeChange}
                              row>
                              <FormControlLabel
                                value={ProductType.Product}
                                disabled={areInitialSettingsDisabled()}
                                label="Product type"
                                labelPlacement="end"
                                control={<Radio color="primary" />}
                              />
                              <FormControlLabel
                                value={ProductType.List}
                                disabled={areInitialSettingsDisabled()}
                                label="Subscribers list"
                                labelPlacement="end"
                                control={<Radio color="primary" />}
                              />
                            </RadioGroup>
                          }
                        />
                      </FormControl>
                      <Tooltip
                        title={`Choose 'Product Type' for selecting events from the enabled products (or) 
                    Choose 'Subscriber List' for selecting list${((getEnvVar(
                      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
                        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) &&
                        '/segmented list'
                    ) || ''} of list management`}>
                        <Info size="small" className={classes.tooltip} />
                      </Tooltip>
                    </Grid>
                  )}
                {thisSurvey.productType === ProductType.List ? (
                  <React.Fragment>
                    {gettingAllLists || gettingAllSegmentedLists ? (
                      <SkeletonCardFields numberOfRows={1} />
                    ) : (
                      <Grid item xs={12} className={classes.autocomplete}>
                        <Grid item md={12}>
                          <Autocomplete
                            id="size-small-outlined"
                            size="small"
                            value={getSubscribersListValue()}
                            disabled={isSubscriberListSelectionDisabled()}
                            onChange={handleSubscriberListChange}
                            options={subscribersList || []}
                            getOptionLabel={option => option.name}
                            filterOptions={(options, state) =>
                              filterSubscribersList(options, state)
                            }
                            renderOption={option => (
                              <React.Fragment>
                                {option.listId ? (
                                  <React.Fragment>
                                    <SubdirectoryArrowRightIcon
                                      classes={segListStyle}
                                    />
                                    <ListItemText>{option.name}</ListItemText>
                                  </React.Fragment>
                                ) : (
                                  <ListItemText>{option.name}</ListItemText>
                                )}
                              </React.Fragment>
                            )}
                            renderInput={params => (
                              <TextField
                                {...params}
                                margin="dense"
                                label="Select a subscriber list"
                                variant="outlined"
                                error={shouldShowError('subscriberListId')}
                                helperText={getErrorMessage('subscriberListId')}
                                required
                              />
                            )}
                          />
                        </Grid>
                        <Tooltip title="This survey will be created for the parent list and can be used by all its segmented lists.">
                          <Info
                            size="small"
                            style={{ marginTop: '8px', marginLeft: '12px' }}
                          />
                        </Tooltip>
                      </Grid>
                    )}
                  </React.Fragment>
                ) : (
                  <Grid item>
                    <SelectInput
                      fullWidth
                      label="Survey Event Type"
                      margin="dense"
                      name="schemaIdentifier"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      onChange={handleAutomationChange}
                      required
                      disabled={isCopyFlow() || areInitialSettingsDisabled()}
                      value={thisSurvey.schemaIdentifier || ''}
                      selectProps={{
                        renderValue: option => option
                      }}
                      error={shouldShowError('schemaIdentifier')}
                      helperText={getErrorMessage('schemaIdentifier') || ''}
                      options={getEventTypeOptions(
                        supportedInternalSurveyResponseTypeOptions
                      )}
                    />
                  </Grid>
                )}
                <FormControl className={classes.logoText}>
                  <InputLabel>Survey Logo</InputLabel>
                  <Grid className={classes.imageGrid}>
                    <Grid item xs={12} className={classes.item}>
                      <TextField
                        fullWidth
                        type="file"
                        margin="dense"
                        variant="outlined"
                        name="file"
                        accept="image/*"
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={handleLogoChange}
                        disabled={isTemplateReadOnly()}
                        error={
                          shouldShowError('surveyTheme.fileSize') ||
                          shouldShowError('surveyTheme.fileFormat')
                        }
                        helperText={
                          getErrorMessage('surveyTheme.fileSize') ||
                          getErrorMessage('surveyTheme.fileFormat')
                        }
                      />
                      <LightTooltip
                        className={classes.infoIcon}
                        placement="right-start"
                        title="Image file must be in PNG, JPEG or GIF format and no larger than 800 pixels in either dimension. File size is limited to 1MB. File will be removed when copied across context. Recommended image size is 300px * 50px">
                        <InfoIcon />
                      </LightTooltip>
                    </Grid>
                    {thisSurveyPreview && (
                      <img
                        className={classes.preview}
                        src={thisSurveyPreview}
                      />
                    )}
                  </Grid>
                </FormControl>
                <Grid item xs={12} className={classes.item} style={{ marginTop: '8px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!thisSurvey.singleResponse}
                        name="singleResponse"
                        onChange={handleSingleResponseChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        color="primary"
                        disabled={isTemplateReadOnly()}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    style={{ marginBottom: '0px' }}
                    label="Enable single survey response"
                  />{' '}
                  <LightTooltip
                    className={classes.infoIcon}
                    style={{ margin: '0px' }}
                    placement="right-start"
                    title="This option is used to enable only single response per profile/event">
                    <InfoIcon />
                  </LightTooltip>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
          {(next || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isSurveyLoading}
                  link={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Details;
